// src/components/Chat/MessageList.js

import React, { useRef, useEffect, useState } from 'react';
import MarkdownIt from 'markdown-it';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';

import StreamingMessage from '../StreamingMessage';
import DualChatLayout from '../DualChatLayout';

const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
  typographer: true
});

// Функция для извлечения краткого сообщения пользователя из полного контекста файла
const extractUserMessage = (fullContent) => {
  if (!fullContent) return '';
  
  // Если это сообщение с файлом, извлекаем только пользовательский текст
  const fileEndMarker = /--- Конец файла ".*?" ---\n\n/;
  const match = fullContent.match(fileEndMarker);
  
  if (match) {
    // Возвращаем текст после маркера конца файла
    const userText = fullContent.substring(match.index + match[0].length).trim();
    return userText || 'Загружен файл';
  }
  
  // Если это обычное сообщение, возвращаем как есть
  return fullContent;
};

const MessageList = ({
  currentChat,
  streamingChat,
  dualStreaming,
  onUpdateChat,
}) => {
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  // --- НАЧАЛО ИСПРАВЛЕНИЯ ПРОКРУТКИ ---
  // Ref для отслеживания предыдущего состояния ожидания выбора
  const prevPendingChoiceRef = useRef(dualStreaming.pendingDualChoice);

  // Эффект для прокрутки, который теперь работает правильно
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    // Определяем, был ли только что сделан выбор в дуал-режиме
    // Это происходит, когда предыдущее состояние было 'true', а текущее стало 'false'
    const choiceWasJustMade = prevPendingChoiceRef.current && !dualStreaming.pendingDualChoice;

    // Условие для "умной" прокрутки: скроллим, если пользователь уже внизу.
    const isScrolledToBottom = container.scrollHeight - container.clientHeight <= container.scrollTop + 150;

    // Условия для принудительной прокрутки
    const forceScroll = 
        streamingChat.isStreaming ||
        dualStreaming.isDualStreaming ||
        choiceWasJustMade; // <-- Наше новое ключевое условие

    if (isScrolledToBottom || forceScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    // Обновляем ref с предыдущим состоянием для следующего рендера
    prevPendingChoiceRef.current = dualStreaming.pendingDualChoice;

  }, [
    currentChat.messages,
    streamingChat.isStreaming,
    streamingChat.streamingText,
    dualStreaming.isDualStreaming,
    dualStreaming.dualStreamingData,
    dualStreaming.pendingDualChoice, // <-- Добавили зависимость
  ]);

  // Эффект для принудительной прокрутки при смене чата
  useEffect(() => {
    setTimeout(() => {
        messagesEndRef.current?.scrollIntoView();
    }, 0);
  }, [currentChat._id]);

  // --- КОНЕЦ ИСПРАВЛЕНИЯ ПРОКРУТКИ ---

  const renderMarkdown = (text) => ({ __html: md.render(text || '') });

  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const timeString = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    
    if (date.toDateString() === today.toDateString()) return `сегодня в ${timeString}`;
    if (date.toDateString() === yesterday.toDateString()) return `вчера в ${timeString}`;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}.${month} в ${timeString}`;
  };

  const copyToClipboard = (text, messageIndex) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageId(messageIndex);
      setTimeout(() => setCopiedMessageId(null), 2000);
      toast.success('Текст скопирован!');
    }).catch(err => {
      console.error('Ошибка при копировании текста:', err);
      toast.error('Не удалось скопировать текст');
    });
  };

  const handleDualChoiceSelection = async (modelKey, content, modelId) => {
    try {
      const actualModelId = modelId || dualStreaming.dualStreamingData[modelKey]?.modelId;
      const result = await dualStreaming.saveChosenResponse(currentChat._id, modelKey, content, actualModelId);
      if (result?.chat) onUpdateChat(result.chat);
      toast.success(`Выбран ответ от ${modelKey === 'model1' ? 'первой' : 'второй'} модели`);
    } catch (error) {
      console.error('Ошибка сохранения выбора:', error);
      toast.error('Ошибка сохранения выбора');
    }
  };

  const handleStopDualStreaming = async () => {
    const updatedChat = await dualStreaming.stopDualStreaming(currentChat._id);
    if (updatedChat) onUpdateChat(updatedChat);
  };

  return (
    <div className="chat-messages" ref={chatContainerRef}>
      <div className="chat-messages-content">
        {currentChat.messages.length === 0 && !streamingChat.isStreaming && !dualStreaming.isDualStreaming ? (
          <div className="text-center py-5">
            <p className="lead">Отправьте сообщение, чтобы начать чат</p>
          </div>
        ) : (
          currentChat.messages.map((msg, index) => {
            // Для пользовательских сообщений используем displayContent если есть, иначе извлекаем краткий текст
            let displayContent;
            if (msg.role === 'user') {
              // Отключаем избыточное логирование
              // console.log('🔍 [MESSAGE LIST] Сообщение пользователя:', {
              //   hasDisplayContent: !!msg.displayContent,
              //   displayContent: msg.displayContent,
              //   contentLength: msg.content?.length,
              //   contentPreview: msg.content?.substring(0, 100) + '...',
              //   messageId: msg._id,
              //   timestamp: msg.timestamp
              // });
              displayContent = msg.displayContent || extractUserMessage(msg.content);
            } else {
              displayContent = msg.content;
            }
            
            return (
              <div key={msg._id || index} className={`message ${msg.role === 'user' ? 'user' : msg.role === 'system' ? 'system processing' : 'assistant'}`}>
                {msg.role === 'system' ? (
                  <div className="message-content">{displayContent}</div>
                ) : (
                  <>
                    <div className="message-content markdown-content" dangerouslySetInnerHTML={renderMarkdown(displayContent)} />
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <div className="message-time text-muted small">{formatMessageDate(msg.timestamp)}</div>
                      <button className="btn btn-sm btn-link text-muted copy-button-inline" onClick={() => copyToClipboard(msg.content, index)} title={copiedMessageId === index ? "Скопировано!" : "Скопировать полное сообщение"}>
                        <FontAwesomeIcon icon={copiedMessageId === index ? faCheck : faCopy} style={copiedMessageId === index ? { color: '#4CAF50' } : {}} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}

        {streamingChat.isStreaming && (
          <StreamingMessage
            streamingText={streamingChat.streamingText}
            isStreaming={streamingChat.isStreaming}
            tokenCount={streamingChat.tokenCount}
            onCancel={streamingChat.stopStreaming}
            className="streaming-message"
          />
        )}

        {(dualStreaming.isDualStreaming || dualStreaming.pendingDualChoice) && (
          <DualChatLayout
            currentChat={currentChat}
            model1Data={dualStreaming.dualStreamingData.model1}
            model2Data={dualStreaming.dualStreamingData.model2}
            isDualStreaming={dualStreaming.isDualStreaming}
            onChoose={handleDualChoiceSelection}
            onCancel={handleStopDualStreaming}
            formatMessageDate={formatMessageDate}
            copyToClipboard={copyToClipboard}
            copiedMessageId={copiedMessageId}
            currentUserMessage={dualStreaming.currentUserMessage}
          />
        )}
        
        <div ref={messagesEndRef} style={{ height: '1px' }} />
      </div>
    </div>
  );
};

export default MessageList;